import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import Typography from "@mui/material/Typography";

import { paths } from "src/routes/paths";
import { RouterLink } from "src/routes/components";

import Logo from "src/components/logo";
import Iconify from "src/components/iconify";
import { useTranslate } from "src/locales";
import Image from "src/components/image";
import { useEffect, useState } from "react";
import { getCookie } from "src/utils/global-functions";
import { useConsent } from "src/components/consent/ConsentProvider";
import { Fab } from "@mui/material";

// ----------------------------------------------------------------------

const footerLinks = [
  {
    headline: "company_domain_name",
    children: [
      // { name: "menu_introduction", href: paths.introduction },
      { name: "about_us", href: paths.about },
      { name: "contact_us", href: paths.contact },
      { name: "faqs", href: paths.faqs },
    ],
  },
  {
    headline: "legal",
    children: [
      { name: "terms_and_conditions", href: paths.terms_and_conditions },
      { name: "privacy_policy", href: paths.privacy_policy },
      { name: "code_of_ethics", href: paths.code_of_ethics },
      // { name: "user_manual", href: paths.user_manual },
    ],
  },
  {
    headline: "contacts",
    children: [{ name: "info@foggosport.com", href: "#" }],
  },
];

// ----------------------------------------------------------------------

const VERSION_KEY = "fe_version_number";

export default function Footer() {
  const { t } = useTranslate();

  const theme = useTheme();

  const storedVersion = getCookie(VERSION_KEY) ?? "0";

  const floatStoredVersion = parseFloat(storedVersion);

  const [floatFrontendVersion, setFloatFrontendVersion] = useState(0);

  useEffect(() => {
    let active = true;

    fetch("/version.json", { cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (active) {
          setFloatFrontendVersion(parseFloat(data.version));
        }
      })
      .catch((error) => {
        console.error("Failed to load frontend version:", error);
      });

    return () => {
      active = false;
    };
  }, []);

  // fetch("/version.json", { cache: "no-store" })
  //   .then((res) => res.json())
  //   .then((data) => {
  //     setFloatFrontendVersion(parseFloat(data.version));
  //   });

  const mainFooter = (
    <Box
      component="footer"
      sx={{
        position: "relative",
        //bgcolor: "background.default",
        bgcolor: theme.palette.grey[100],
        mt: 8,
      }}
    >
      <Divider />

      <Container
        sx={{
          pt: 10,
          pb: 5,
          textAlign: { xs: "center", md: "unset" },
        }}
      >
        <Stack
          direction="row"
          justifyContent={"center"}
          alignItems={"center"}
          spacing={5}
          sx={{ mb: 3 }}
        >
          <Logo sx={{ mb: 0 }} />
          {/* <LanguagePopover /> */}
          <CookieSettingsFab />
        </Stack>
        <Grid
          container
          justifyContent={{
            xs: "center",
            md: "space-between",
          }}
        >
          <Grid xs={8} md={3}>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 270,
                mx: { xs: "auto", md: "unset" },
              }}
            >
              {t("footer_main_text")}
            </Typography>

            <Stack
              direction="row"
              justifyContent={{ xs: "center", md: "flex-start" }}
              sx={{
                mt: 3,
                mb: { xs: 5, md: 0 },
              }}
            >
              {/* {_socials.map((social) => (
                <IconButton
                  key={social.name}
                  sx={{
                    "&:hover": {
                      bgcolor: alpha(social.color, 0.08),
                    },
                  }}
                >
                  <Iconify color={social.color} icon={social.icon} />
                </IconButton>
              ))} */}
            </Stack>
          </Grid>

          <Grid xs={12} md={6}>
            <Stack spacing={5} direction={{ xs: "column", md: "row" }}>
              {footerLinks.map((list) => (
                <Stack
                  key={list.headline}
                  spacing={2}
                  alignItems={{ xs: "center", md: "flex-start" }}
                  sx={{ width: 1 }}
                >
                  <Typography component="div" variant="overline">
                    {t(list.headline)}
                  </Typography>

                  {list.children.map((link) => (
                    <Link
                      key={link.name}
                      component={RouterLink}
                      href={link.href}
                      color="inherit"
                      variant="body2"
                    >
                      {t(link.name)}
                    </Link>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Stack direction={"row"} sx={{ mt: 10 }} spacing={2}>
          <Typography variant="body2">
            {t("footer_copyright_text")} 2016 - {new Date().getFullYear()}
          </Typography>
          {!!floatStoredVersion && (
            <Typography variant="body2">V: {floatStoredVersion}</Typography>
          )}
          *
          {!floatStoredVersion && (
            <Typography variant="body2">V. {floatFrontendVersion}</Typography>
          )}
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }}>
          <Image
            sx={{ my: 2 }}
            alt={"barion_logo"}
            src={"/assets/images/barion-card-strip-intl__small.png"}
          />
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={"center"}
        >
          <Image
            alt={"/assets/images/eu_flag_co_funded_pos_rgb_right.jpg"}
            src={"/assets/images/eu_flag_co_funded_pos_rgb_right.jpg"}
            // sx={{ borderRadius: 1.5, height: { xs: 70, md: 60 } }}
            sx={{
              borderRadius: 1.5,
              width: "100%", // take full width of container
              maxWidth: 550, // but never exceed 550px
              height: "auto", // keep aspect ratio
              display: "block", // avoids inline-gap issues
              mx: "auto", // centers horizontally
            }}
          />
          <Typography variant="caption">
            Funded by the European Union. Views and opinions expressed are
            however those of the author(s) only and do not necessarily reflect
            those of the European Union or the European Education and Culture
            Executive Agency (EACEA). Neither the European Union nor EACEA can
            be held responsible for them.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );

  //return homePage ? simpleFooter : mainFooter;
  return mainFooter;
}

function CookieSettingsFab() {
  const { openPreferences } = useConsent();
  return (
    <Fab
      color="default"
      size="small"
      onClick={openPreferences}
      aria-label="Cookie settings"
    >
      <Iconify icon="material-symbols:settings" />
    </Fab>
  );
}
